import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  STORE_JOB_ROLES,
  TIP_POOLS,
  TIP_POOL_RULES,
  CREATE_TIP_POOL,
  UPDATE_TIP_POOL,
  DELETE_TIP_POOL,
  CREATE_OR_UPDATE_TIP_POOL_RULE,
  DELETE_TIP_POOL_RULE,
  RUN_TIP_ALLOCATION,
} from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import type {
  StoreJobRole,
  TipPool,
  TipPoolRule,
  TipPoolInput,
  TipPoolRuleInput,
  TipPoolTypeEnum,
} from "../types/payroll.types";

const POOL_TYPE_LABELS: Record<TipPoolTypeEnum, string> = {
  SALES_PERCENT: "Sales %",
  TIP_PERCENT: "Tip %",
  ALL_TIPS_HOURS_WEIGHTED: "Hours weighted",
};

export function TipPoolsTab() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [pools, setPools] = useState<TipPool[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null);
  const [rules, setRules] = useState<TipPoolRule[]>([]);
  const [jobRoles, setJobRoles] = useState<StoreJobRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poolModalOpen, setPoolModalOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<TipPool | null>(null);
  const [poolForm, setPoolForm] = useState<TipPoolInput>({
    name: "",
    poolType: "TIP_PERCENT",
    isActive: true,
  });
  const [runDate, setRunDate] = useState("");
  const [runResult, setRunResult] = useState<string | null>(null);

  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TipPoolRule | null>(null);
  const [ruleForm, setRuleForm] = useState<TipPoolRuleInput>({
    percentOfSales: null,
    percentOfTips: null,
    weight: null,
  });
  const [ruleJobRoleId, setRuleJobRoleId] = useState<string>("");
  const [activateConfirmPool, setActivateConfirmPool] = useState<TipPool | null>(null);

  const activePool = useMemo(() => pools.find((p) => p.isActive) ?? null, [pools]);

  const rulesJobRoleIds = useMemo(() => new Set(rules.map((r) => r.storeJobRoleId)), [rules]);
  const availableJobRolesForRule = jobRoles.filter((j) => !rulesJobRoleIds.has(j.id));
  const canAddRule = selectedPoolId != null && availableJobRolesForRule.length > 0;

  const totalSalesPercentConfigured = useMemo(() => {
    const pool = selectedPoolId != null ? pools.find((p) => p.id === selectedPoolId) : undefined;
    if (!pool || pool.poolType !== "SALES_PERCENT") return null;
    const sum = rules.reduce((acc, r) => acc + (r.percentOfSales ?? 0), 0);
    return sum;
  }, [selectedPoolId, pools, rules]);

  const fetchPools = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axBe.post<{ data?: { tipPools?: TipPool[] } }>(GRAPHQL_ENDPOINT, {
        query: TIP_POOLS,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: false },
      });
      setPools(data?.data?.tipPools ?? []);
      if (!selectedPoolId && (data?.data?.tipPools?.length ?? 0) > 0) {
        setSelectedPoolId(data?.data?.tipPools?.[0]?.id ?? null);
      }
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to load tip pools");
      setPools([]);
    } finally {
      setLoading(false);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchJobRoles = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeJobRoles?: StoreJobRole[] } }>(GRAPHQL_ENDPOINT, {
        query: STORE_JOB_ROLES,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: true },
      });
      setJobRoles(data?.data?.storeJobRoles ?? []);
    } catch {
      setJobRoles([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchRules = useCallback(async () => {
    if (selectedPoolId == null) {
      setRules([]);
      return;
    }
    setRulesLoading(true);
    try {
      const { data } = await axBe.post<{ data?: { tipPoolRules?: TipPoolRule[] } }>(GRAPHQL_ENDPOINT, {
        query: TIP_POOL_RULES,
        variables: { tipPoolId: selectedPoolId },
      });
      setRules(data?.data?.tipPoolRules ?? []);
    } catch {
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [selectedPoolId, axBe]);

  useEffect(() => {
    fetchPools();
    fetchJobRoles();
  }, [fetchPools, fetchJobRoles]);
  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const selectedPool = pools.find((p) => p.id === selectedPoolId);

  const openAddPool = () => {
    setEditingPool(null);
    const hasActive = pools.some((p) => p.isActive);
    setPoolForm({ name: "", poolType: "TIP_PERCENT", isActive: !hasActive });
    setPoolModalOpen(true);
  };
  const openEditPool = (pool: TipPool) => {
    setEditingPool(pool);
    setPoolForm({
      name: pool.name,
      poolType: pool.poolType,
      isActive: pool.isActive,
    });
    setPoolModalOpen(true);
  };
  const closePoolModal = () => {
    setPoolModalOpen(false);
    setEditingPool(null);
  };

  const handleSavePool = async () => {
    if (!hasRestaurant?.correlationId || !poolForm.name.trim()) return;
    const payload: TipPoolInput = {
      name: poolForm.name.trim(),
      poolType: poolForm.poolType,
      isActive: editingPool ? poolForm.isActive : poolForm.isActive ?? true,
    };
    try {
      if (editingPool) {
        if (payload.isActive && pools.some((p) => p.id !== editingPool.id && p.isActive)) {
          for (const p of pools) {
            if (p.id !== editingPool.id && p.isActive) {
              await axBe.post(GRAPHQL_ENDPOINT, {
                query: UPDATE_TIP_POOL,
                variables: { id: p.id, input: { name: p.name, poolType: p.poolType, isActive: false } },
              });
            }
          }
        }
        await axBe.post(GRAPHQL_ENDPOINT, {
          query: UPDATE_TIP_POOL,
          variables: { id: editingPool.id, input: payload },
        });
      } else {
        if (payload.isActive && pools.some((p) => p.isActive)) {
          for (const p of pools) {
            if (p.isActive) {
              await axBe.post(GRAPHQL_ENDPOINT, {
                query: UPDATE_TIP_POOL,
                variables: { id: p.id, input: { name: p.name, poolType: p.poolType, isActive: false } },
              });
            }
          }
        }
        await axBe.post(GRAPHQL_ENDPOINT, {
          query: CREATE_TIP_POOL,
          variables: { storeCorrelationId: hasRestaurant.correlationId, input: payload },
        });
      }
      closePoolModal();
      fetchPools();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to save pool");
    }
  };

  const handleDeletePool = async (id: number) => {
    if (!window.confirm("Delete this tip pool?")) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, { query: DELETE_TIP_POOL, variables: { id } });
      if (selectedPoolId === id) setSelectedPoolId(null);
      fetchPools();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to delete");
    }
  };

  const handleActivatePool = async (pool: TipPool) => {
    setActivateConfirmPool(pool);
  };

  const confirmActivatePool = async () => {
    const pool = activateConfirmPool;
    if (!pool || !hasRestaurant?.correlationId) return;
    setActivateConfirmPool(null);
    try {
      for (const p of pools) {
        if (p.id !== pool.id && p.isActive) {
          await axBe.post(GRAPHQL_ENDPOINT, {
            query: UPDATE_TIP_POOL,
            variables: { id: p.id, input: { name: p.name, poolType: p.poolType, isActive: false } },
          });
        }
      }
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_TIP_POOL,
        variables: {
          id: pool.id,
          input: { name: pool.name, poolType: pool.poolType, isActive: true },
        },
      });
      fetchPools();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to activate pool");
    }
  };

  const openAddRule = () => {
    setEditingRule(null);
    setRuleJobRoleId("");
    setRuleForm({ percentOfSales: null, percentOfTips: null, weight: null });
    setRuleModalOpen(true);
  };
  const openEditRule = (rule: TipPoolRule) => {
    setEditingRule(rule);
    setRuleJobRoleId(String(rule.storeJobRoleId));
    setRuleForm({
      percentOfSales: rule.percentOfSales ?? null,
      percentOfTips: rule.percentOfTips ?? null,
      weight: rule.weight ?? null,
    });
    setRuleModalOpen(true);
  };
  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setEditingRule(null);
  };

  const handleSaveRule = async () => {
    if (selectedPoolId == null || !selectedPool) return;
    const storeJobRoleId = editingRule?.storeJobRoleId ?? (ruleJobRoleId ? parseInt(ruleJobRoleId, 10) : null);
    if (storeJobRoleId == null || Number.isNaN(storeJobRoleId)) return;
    const poolType = selectedPool.poolType;
    const input: TipPoolRuleInput = {
      percentOfSales: poolType === "SALES_PERCENT" && ruleForm.percentOfSales != null ? ruleForm.percentOfSales : null,
      percentOfTips: poolType === "TIP_PERCENT" && ruleForm.percentOfTips != null ? ruleForm.percentOfTips : null,
      weight: poolType === "ALL_TIPS_HOURS_WEIGHTED" && ruleForm.weight != null ? ruleForm.weight : null,
    };
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: CREATE_OR_UPDATE_TIP_POOL_RULE,
        variables: { tipPoolId: selectedPoolId, storeJobRoleId, input },
      });
      closeRuleModal();
      fetchRules();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to save rule");
    }
  };

  const handleDeleteRule = async (id: number) => {
    if (!window.confirm("Remove this rule?")) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, { query: DELETE_TIP_POOL_RULE, variables: { id } });
      fetchRules();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to delete rule");
    }
  };

  const handleRunAllocation = async () => {
    if (!hasRestaurant?.correlationId || !runDate.trim()) return;
    setRunResult(null);
    try {
      const { data } = await axBe.post<{
        data?: { runTipAllocation?: { success?: boolean; message?: string; allocationsCount?: number } };
      }>(GRAPHQL_ENDPOINT, {
        query: RUN_TIP_ALLOCATION,
        variables: { storeCorrelationId: hasRestaurant.correlationId, businessDate: runDate },
      });
      const result = data?.data?.runTipAllocation;
      if (result?.success) {
        setRunResult(`Success. Rows allocated: ${result.allocationsCount ?? "—"}. ${result.message ?? ""}`);
      } else {
        setRunResult(result?.message ?? "Allocation did not succeed.");
      }
    } catch (e: unknown) {
      setRunResult((e as { message?: string })?.message ?? "Failed to run allocation");
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {runResult && (
        <div className="mb-4 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3 text-sm text-[#374151]">
          {runResult}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <h3 className="text-sm font-semibold text-[#374151]">Tip pools</h3>
        <button
          type="button"
          onClick={openAddPool}
          className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95"
        >
          Add pool
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-[#6b7280]">Loading pools…</p>
      ) : (
        <div className="rounded-lg border border-[#e5e7eb]">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-left border-collapse">
            <thead>
              <tr className="bg-[#f9fafb] border-b border-[#e5e7eb]">
                <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Name</th>
                <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Type</th>
                <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Active</th>
                <th className="py-3 px-4 text-xs font-medium text-[#6b7280] whitespace-nowrap w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pools.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[#6b7280] text-sm">
                    No tip pools. Add one to get started.
                  </td>
                </tr>
              ) : (
                pools.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => setSelectedPoolId(p.id)}
                    className={`border-t border-[#f3f4f6] hover:bg-[#f9fafb] cursor-pointer ${selectedPoolId === p.id ? "bg-[#fafbff] border-l-4 border-l-[#FD7657]" : ""}`}
                  >
                    <td className="py-3 px-4 text-sm font-medium text-[#111827]">{p.name}</td>
                    <td className="py-3 px-4 text-sm text-[#374151]">{POOL_TYPE_LABELS[p.poolType]}</td>
                    <td className="py-3 px-4 text-sm">
                      {p.isActive ? (
                        <span className="inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Active
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivatePool(p);
                          }}
                          className="text-[#FD7657] text-sm font-medium hover:underline"
                        >
                          Set active
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditPool(p)}
                          className="text-[#FD7657] text-sm font-medium hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePool(p.id)}
                          className="text-red-600 text-sm font-medium hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {selectedPool && (
        <>
          <div className="mt-10 pt-6 border-t border-[#e5e7eb]">
            <h2 className="text-lg font-semibold text-[#111827] capitalize">{selectedPool.name}</h2>
            <p className="text-sm text-[#6b7280] mt-0.5">
              {POOL_TYPE_LABELS[selectedPool.poolType]} Pool
            </p>
          </div>

          <div className="mt-8">
            <h4 className="text-sm font-semibold text-[#374151] mb-1">Distribution rules</h4>
            <p className="text-sm text-[#374151] mb-3 max-w-xl">
              {selectedPool.poolType === "SALES_PERCENT"
                ? "Each role receives this % of store sales (split by hours; total allocation cannot exceed tips)."
                : selectedPool.poolType === "TIP_PERCENT"
                  ? "Each role receives this % of total tips (split by hours within the role)."
                  : "Weight per role; allocation is split by weighted hours across all roles."}
            </p>
            {selectedPool.poolType === "SALES_PERCENT" && totalSalesPercentConfigured != null && (
              <div className="mb-3 space-y-1">
                <p className="text-sm text-[#6b7280]">
                  Total allocation configured: <span className="font-medium text-[#111827]">{totalSalesPercentConfigured}%</span>
                  {totalSalesPercentConfigured < 100 && (
                    <> · Unallocated portion: <span className="font-medium text-[#111827]">{100 - totalSalesPercentConfigured}%</span></>
                  )}
                </p>
                {totalSalesPercentConfigured > 100 && (
                  <p className="text-sm text-amber-700 flex items-center gap-1.5" role="alert">
                    <span aria-hidden>⚠</span>
                    Total allocation exceeds 100% of sales. Allocation will be capped by total tips collected.
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span />
              <button
                type="button"
                onClick={openAddRule}
                disabled={!canAddRule}
                className="px-3 py-1.5 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
              >
                Add rule
              </button>
            </div>
            {rulesLoading ? (
              <p className="text-sm text-[#6b7280]">Loading rules…</p>
            ) : (
              <div className="rounded-lg border border-[#e5e7eb]">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left border-collapse">
                  <thead>
                    <tr className="bg-[#f9fafb] border-b border-[#e5e7eb]">
                      <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Job role</th>
                      {selectedPool.poolType === "SALES_PERCENT" && (
                        <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">% of total sales (to this role)</th>
                      )}
                      {selectedPool.poolType === "TIP_PERCENT" && (
                        <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">% of tips</th>
                      )}
                      {selectedPool.poolType === "ALL_TIPS_HOURS_WEIGHTED" && (
                        <th className="py-3 px-4 text-xs font-medium text-[#6b7280]">Weight</th>
                      )}
                      <th className="py-3 px-4 text-xs font-medium text-[#6b7280] whitespace-nowrap w-28 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-[#6b7280] text-sm">
                          No rules. Add a rule to set percentages per job role.
                        </td>
                      </tr>
                    ) : (
                      rules.map((r) => (
                        <tr key={r.id} className="border-t border-[#f3f4f6] hover:bg-[#f9fafb]">
                          <td className="py-3 px-4 text-sm text-[#111827]">{r.jobRoleName ?? r.storeJobRoleId}</td>
                          {selectedPool.poolType === "SALES_PERCENT" && (
                            <td className="py-3 px-4 text-sm">{r.percentOfSales != null ? `${r.percentOfSales}%` : "—"}</td>
                          )}
                          {selectedPool.poolType === "TIP_PERCENT" && (
                            <td className="py-3 px-4 text-sm">{r.percentOfTips != null ? `${r.percentOfTips}%` : "—"}</td>
                          )}
                          {selectedPool.poolType === "ALL_TIPS_HOURS_WEIGHTED" && (
                            <td className="py-3 px-4 text-sm">{r.weight ?? "—"}</td>
                          )}
                          <td className="py-3 px-4 whitespace-nowrap text-right">
                            <div className="inline-flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditRule(r)}
                                className="text-[#FD7657] text-sm font-medium hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteRule(r.id)}
                                className="text-red-600 text-sm font-medium hover:underline"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            <div className="mt-8 p-4 rounded-lg border border-[#e5e7eb] bg-[#fcfdfd]">
              {activePool ? (
                <p className="text-sm text-[#374151] mb-2">
                  Currently active pool: <span className="font-medium text-[#111827]">{activePool.name}</span>{" "}
                  <span className="text-[#6b7280]">({POOL_TYPE_LABELS[activePool.poolType]})</span>
                </p>
              ) : (
                <p className="text-sm text-amber-700 mb-2" role="status">
                  No active pool. Set a pool as active above to run allocation.
                </p>
              )}
              <h5 className="text-sm font-medium text-[#374151] mb-1">Run tip allocation</h5>
              <p className="text-xs text-[#6b7280] mb-3">Run allocation for a business date. Results apply to that date for this store.</p>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs font-medium text-[#6b7280] mb-1">Business date</label>
                  <input
                    type="date"
                    value={runDate}
                    onChange={(e) => setRunDate(e.target.value)}
                    className="border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleRunAllocation}
                  disabled={!activePool || !runDate.trim()}
                  className="px-4 py-2 rounded-lg bg-[#059669] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
                >
                  Run allocation
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && pools.length > 0 && !selectedPool && (
        <p className="mt-6 text-sm text-[#6b7280]">Select a pool above to configure rules and run allocation.</p>
      )}

      {activateConfirmPool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl border border-[#E5E7EB] w-full max-w-md p-6 shadow-[0_20px_40px_rgba(0,0,0,0.12)]">
            <h3 className="text-lg font-semibold text-[#111827] mb-2">Activate this pool?</h3>
            <p className="text-sm text-[#6b7280] mb-4">
              Allocation runs use only one active pool. Activating <span className="font-medium text-[#111827]">{activateConfirmPool.name}</span> will
              deactivate the current active pool.
              {pools.filter((p) => p.id !== activateConfirmPool.id && p.isActive).length > 0 && (
                <span className="block mt-2">
                  This will deactivate:{" "}
                  {pools
                    .filter((p) => p.id !== activateConfirmPool.id && p.isActive)
                    .map((p) => `${p.name} (${POOL_TYPE_LABELS[p.poolType]})`)
                    .join(", ")}
                </span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setActivateConfirmPool(null)}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmActivatePool}
                className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {poolModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-[#111827] mb-4">
              {editingPool ? "Edit tip pool" : "Add tip pool"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Name</label>
                <input
                  type="text"
                  value={poolForm.name}
                  onChange={(e) => setPoolForm((f: TipPoolInput) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. Front of house"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Pool type</label>
                <select
                  value={poolForm.poolType}
                  onChange={(e) =>
                    setPoolForm((f: TipPoolInput) => ({ ...f, poolType: e.target.value as TipPoolTypeEnum }))
                  }
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                >
                  <option value="SALES_PERCENT">Sales %</option>
                  <option value="TIP_PERCENT">Tip %</option>
                  <option value="ALL_TIPS_HOURS_WEIGHTED">Hours weighted</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="poolIsActive"
                  checked={poolForm.isActive ?? false}
                  onChange={(e) =>
                    setPoolForm((f: TipPoolInput) => ({ ...f, isActive: e.target.checked }))
                  }
                  className="rounded border-[#e5e7eb]"
                />
                <label htmlFor="poolIsActive" className="text-sm text-[#374151]">
                  Set as active pool (only one active pool is used for allocation)
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closePoolModal}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePool}
                disabled={!poolForm.name.trim()}
                className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {ruleModalOpen && selectedPool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-[#111827] mb-1">
              {editingRule ? "Edit rule" : "Add rule"}
            </h3>
            <p className="text-xs text-[#6b7280] mb-2">
              {selectedPool.poolType === "SALES_PERCENT"
                ? `Pool “${selectedPool.name}” uses ${POOL_TYPE_LABELS[selectedPool.poolType]}. These roles receive a percentage of total store sales. Set the share per role below.`
                : `Pool “${selectedPool.name}” uses ${POOL_TYPE_LABELS[selectedPool.poolType]}. Set the value per job role below.`}
            </p>
            {selectedPool.poolType === "SALES_PERCENT" && (
              <p className="text-xs text-[#6b7280] mb-4 italic">
                Note: In many Sales % setups, support roles receive a % of sales and servers keep remaining tips outside the pool.
              </p>
            )}
            <div className="space-y-4">
              {editingRule ? (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Job role</label>
                  <p className="text-sm text-[#374151] py-2">{editingRule.jobRoleName ?? `Role #${editingRule.storeJobRoleId}`}</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Job role</label>
                  <select
                    value={ruleJobRoleId}
                    onChange={(e) => setRuleJobRoleId(e.target.value)}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select role</option>
                    {availableJobRolesForRule.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedPool.poolType === "SALES_PERCENT" && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">
                    % of total sales allocated to this role
                  </label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    max={100}
                    value={ruleForm.percentOfSales != null ? String(ruleForm.percentOfSales) : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRuleForm((f) => ({ ...f, percentOfSales: v === "" ? null : (parseFloat(v) ?? null) }));
                    }}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 2"
                  />
                  <p className="text-xs text-[#6b7280] mt-1">
                    Example: 2% means this role receives 2% of store sales for the period. Allocation is capped by
                    available tips.
                  </p>
                </div>
              )}
              {selectedPool.poolType === "TIP_PERCENT" && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">% of tips</label>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    max={100}
                    value={ruleForm.percentOfTips != null ? String(ruleForm.percentOfTips) : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRuleForm((f) => ({ ...f, percentOfTips: v === "" ? null : (parseFloat(v) ?? null) }));
                    }}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 5"
                  />
                </div>
              )}
              {selectedPool.poolType === "ALL_TIPS_HOURS_WEIGHTED" && (
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Weight</label>
                  <input
                    type="number"
                    step={0.1}
                    min={0}
                    value={ruleForm.weight != null ? String(ruleForm.weight) : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRuleForm((f) => ({ ...f, weight: v === "" ? null : (parseFloat(v) ?? null) }));
                    }}
                    className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 1"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeRuleModal}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRule}
                disabled={!editingRule && !ruleJobRoleId}
                className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
